import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts'
import AppDropdown from '../../../components/AppDropdown'
import { AppTooltip, ChartTooltipSurface } from '../../../components/AppTooltip'
import useBodyScrollLock from '../../../hooks/useBodyScrollLock'
import { useProjectAnalytics } from './hooks/useProjectAnalytics'
import './ProjectAnalyticsScreen.css'

const BILLING_SERIES_COLOR = 'var(--primary-container)'
const COST_SERIES_COLOR = '#94a3b8'

function clampPercent(value) {
  const parsed = Number(value || 0)
  if (!Number.isFinite(parsed)) return 0
  return Math.max(0, Math.min(100, parsed))
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 })
}

function formatCompactCurrency(value) {
  const amount = Number(value || 0)
  const abs = Math.abs(amount)
  if (abs >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`
  if (abs >= 100000) return `₹${(amount / 100000).toFixed(1)}L`
  if (abs >= 1000) return `₹${(amount / 1000).toFixed(1)}K`
  return formatCurrency(amount)
}

function titleCase(text) {
  return String(text || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}

function AnalyticsProjectTable({ columns, rows, rowKey, renderRow, emptyMessage = 'No records match the current filters.', className = '' }) {
  return (
    <div className={`analytics-project-list-card ${className}`}>
      <div className="analytics-project-list-scroll">
        <table className="analytics-project-list-table">
          <thead>
            <tr>
              {columns.map((column) => <th key={column}>{column}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((row, index) => <tr key={rowKey(row, index)}>{renderRow(row, index)}</tr>) : (
              <tr><td className="analytics-project-list-empty" colSpan={columns.length}>{emptyMessage}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function CompletionRing({ percent = 0, label, colorClass, tooltip }) {
  const normalized = clampPercent(percent)
  const circumference = 2 * Math.PI * 56
  const dashOffset = circumference - (normalized / 100) * circumference
  return (
    <AppTooltip content={tooltip}>
      <div className="relative h-32 w-32">
        <svg className="h-full w-full -rotate-90">
          <circle cx="64" cy="64" r="56" className="text-surface-container-high" fill="transparent" stroke="currentColor" strokeWidth="12" />
          <circle
            cx="64"
            cy="64"
            r="56"
            className={colorClass}
            fill="transparent"
            stroke="currentColor"
            strokeWidth="12"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold font-headline">{Math.round(normalized)}%</span>
          <span className="text-[10px] font-bold uppercase text-on-surface-variant">{label}</span>
        </div>
      </div>
    </AppTooltip>
  )
}

function CostVsBillingTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const item = payload[0]?.payload || {}
  return (
    <ChartTooltipSurface className="analytics-project-tooltip">
      <p className="analytics-project-tooltip__title">{label}</p>
      {item.periodStart && item.periodEnd ? (
        <p className="text-[10px] text-on-surface-variant mb-1">{item.periodStart} to {item.periodEnd}</p>
      ) : null}
      <div className="analytics-project-tooltip__row">
        <span className="analytics-project-tooltip__series"><span className="analytics-project-tooltip__dot analytics-project-tooltip__dot--billing" />Billing</span>
        <strong>{formatCurrency(item.billing)}</strong>
      </div>
      <div className="analytics-project-tooltip__row">
        <span className="analytics-project-tooltip__series"><span className="analytics-project-tooltip__dot analytics-project-tooltip__dot--cost" />Cost</span>
        <strong>{formatCurrency(item.cost)}</strong>
      </div>
      <div className="analytics-project-tooltip__row">
        <span>Margin</span>
        <strong>{formatCurrency(item.margin)}</strong>
      </div>
    </ChartTooltipSurface>
  )
}

function SurplusTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const item = payload[0]?.payload || {}
  return (
    <ChartTooltipSurface className="analytics-project-tooltip">
      <p className="analytics-project-tooltip__title">{label}</p>
      {item.periodStart && item.periodEnd ? (
        <p className="text-[10px] text-on-surface-variant mb-1">{item.periodStart} to {item.periodEnd}</p>
      ) : null}
      <div className="analytics-project-tooltip__row">
        <span className="analytics-project-tooltip__series"><span className="analytics-project-tooltip__dot analytics-project-tooltip__dot--billing" />Inflow</span>
        <strong>{formatCurrency(item.inflow)}</strong>
      </div>
      <div className="analytics-project-tooltip__row">
        <span className="analytics-project-tooltip__series"><span className="analytics-project-tooltip__dot analytics-project-tooltip__dot--cost" />Outflow</span>
        <strong>{formatCurrency(item.outflow)}</strong>
      </div>
      <div className="analytics-project-tooltip__row">
        <span className="analytics-project-tooltip__series"><span className="analytics-project-tooltip__dot analytics-project-tooltip__dot--surplus" />Net Cash</span>
        <strong>{formatCurrency(item.net ?? item.margin)}</strong>
      </div>
    </ChartTooltipSurface>
  )
}

export default function ProjectAnalyticsScreen() {
  const navigate = useNavigate()
  const { state, actions } = useProjectAnalytics()
  useBodyScrollLock(state.isBreakdownOpen || state.isDetailsOpen)
  const insights = state.insights || {}
  const completionHealth = insights.completionHealth || {}
  const costVsBilling = insights.costVsBilling || {}
  const surplusTrend = insights.surplusTrend || {}
  const resourceLoad = insights.resourceLoad || {}
  const paymentForecast = insights.paymentForecast || {}

  const groupedCostSeries = costVsBilling.seriesByGroup || {}
  const selectedCostSeries = groupedCostSeries[state.costBillingGroup]
  const costSeries = Array.isArray(selectedCostSeries)
    ? selectedCostSeries
    : Array.isArray(costVsBilling.series) ? costVsBilling.series : []
  const surplusSeries = Array.isArray(surplusTrend.series) ? surplusTrend.series : []
  const bestBucket = surplusTrend.bestBucket || null
  const worstBucket = surplusTrend.worstBucket || null
  const loadSeries = Array.isArray(resourceLoad.series) ? resourceLoad.series : []
  const resourceUsers = Array.isArray(resourceLoad.users) ? resourceLoad.users : []
  const heatmapRows = useMemo(() => loadSeries.slice(-28), [loadSeries])
  const completionGrowth = Number(completionHealth.growthVsLastYearPercent || 0)
  const completionHasComparison = Boolean(completionHealth.comparisonHasData)
  const completionTrendIcon = completionGrowth >= 0 ? 'trending_up' : 'trending_down'
  const completionTrendTone = completionGrowth >= 0 ? 'text-emerald-600' : 'text-error'

  const drillColumns = Array.isArray(state.drilldown?.columns) ? state.drilldown.columns : []
  const drillRows = Array.isArray(state.drilldown?.rows) ? state.drilldown.rows : []
  const canViewFinancial = Boolean(insights.permissions?.canViewFinancial)

  return (
    <div className="w-full max-w-[1600px] px-8 py-8">
      <section className="mb-10 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div className="max-w-2xl">
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-primary">Bridge Meridian</p>
          <h2 className="mb-2 font-headline text-5xl font-extrabold tracking-tight text-on-surface">Project Analytics</h2>
          <p className="text-lg text-on-surface-variant">Real-time health indicators and financial projections across the enterprise.</p>
        </div>
        <div className="glass-panel flex flex-wrap items-center gap-3 rounded-xl border border-white/40 p-3 shadow-sm">
          <div className="flex flex-col px-3 py-1">
            <span className="text-[10px] font-bold uppercase text-on-surface-variant">Project</span>
            <AppDropdown
              value={state.projectId}
              onChange={(value) => actions.setProjectId(String(value))}
              options={state.projectOptions}
              placeholder="All Strategic Projects"
              triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-bold text-primary shadow-none"
              menuClassName="min-w-[240px]"
            />
          </div>
          <div className="h-8 w-px bg-outline-variant/30" />
          <div className="flex flex-col px-3 py-1">
            <span className="text-[10px] font-bold uppercase text-on-surface-variant">Customer</span>
            <AppDropdown value={state.customer} onChange={(value) => actions.setCustomer(String(value))} options={state.customerOptions} placeholder="All Customers" triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-bold text-primary shadow-none" menuClassName="min-w-[200px]" />
          </div>
          <div className="flex flex-col px-3 py-1">
            <span className="text-[10px] font-bold uppercase text-on-surface-variant">Project Manager</span>
            <AppDropdown value={state.projectManagerId} onChange={(value) => actions.setProjectManagerId(String(value))} options={state.projectManagerOptions} placeholder="All Managers" triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-bold text-primary shadow-none" menuClassName="min-w-[200px]" />
          </div>
          <div className="flex flex-col px-3 py-1">
            <span className="text-[10px] font-bold uppercase text-on-surface-variant">Status</span>
            <AppDropdown value={state.status} onChange={(value) => actions.setStatus(String(value))} options={state.statusOptions} placeholder="All Statuses" triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-bold text-primary shadow-none" menuClassName="min-w-[160px]" />
          </div>
          <div className="h-8 w-px bg-outline-variant/30" />
          <div className="flex flex-col px-3 py-1">
            <span className="text-[10px] font-bold uppercase text-on-surface-variant">Date Range</span>
            <AppDropdown
              value={state.presetDays}
              onChange={(value) => actions.applyPreset(String(value))}
              options={state.presetOptions}
              placeholder="Last 30 Days"
              triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-bold text-primary shadow-none"
              menuClassName="min-w-[180px]"
            />
          </div>
          <div className="h-8 w-px bg-outline-variant/30" />
          <div className="flex flex-col px-3 py-1">
            <span className="text-[10px] font-bold uppercase text-on-surface-variant">Department</span>
            <AppDropdown
              value={state.departmentId}
              onChange={(value) => actions.setDepartmentId(String(value))}
              options={state.departmentOptions}
              placeholder="All Departments"
              triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-bold text-primary shadow-none"
              menuClassName="min-w-[200px]"
            />
          </div>
          <button
            type="button"
            className="ml-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-white"
            aria-label="Reset filters"
            onClick={actions.resetFilters}
          >
            <span className="material-symbols-outlined">filter_list</span>
          </button>
        </div>
      </section>

      {state.error ? <div className="analytics-project-status analytics-project-status-error mb-4">{state.error}</div> : null}
      {state.isLoading ? <div className="analytics-project-status mb-4">Loading project analytics...</div> : null}
      {!state.isLoading && !canViewFinancial ? <div className="analytics-project-status mb-4">Financial cost, billing, margin and receivable widgets are hidden because your role does not include Dashboard Financial access.</div> : null}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        {/* Completion Health */}
        <div className="flex flex-col justify-between rounded-lg border border-outline-variant/10 bg-surface-container-lowest p-6 shadow-sm md:col-span-4">
          <div>
            <h3 className="mb-1 font-headline text-lg font-bold">Completion Health</h3>
            <p className="mb-6 text-sm text-on-surface-variant">Aggregate velocity metrics across tasks and jobs</p>
          </div>
          <div className="flex items-center justify-around">
            <div
              role="button"
              tabIndex={0}
              onClick={() => actions.openTasks()}
              onKeyDown={(e) => e.key === 'Enter' && actions.openTasks()}
              className="group flex flex-col items-center cursor-pointer transition-transform hover:scale-105"
            >
              <CompletionRing
                percent={completionHealth.tasksPercent}
                label="Tasks"
                colorClass="text-primary"
                tooltip={`Completed ${Number(completionHealth.tasksCompleted || 0)} of ${Number(completionHealth.tasksTotal || 0)} active tasks. Click to view task records.`}
              />
              <span className="mt-2 text-xs font-bold text-on-surface">
                {Number(completionHealth.tasksCompleted || 0)} / {Number(completionHealth.tasksTotal || 0)}
              </span>
              <span className="text-[10px] text-on-surface-variant group-hover:text-primary group-hover:underline">
                View Tasks &rarr;
              </span>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => actions.openJobs()}
              onKeyDown={(e) => e.key === 'Enter' && actions.openJobs()}
              className="group flex flex-col items-center cursor-pointer transition-transform hover:scale-105"
            >
              <CompletionRing
                percent={completionHealth.jobsPercent}
                label="Jobs"
                colorClass="text-primary-container"
                tooltip={`Completed ${Number(completionHealth.jobsCompleted || 0)} of ${Number(completionHealth.jobsTotal || 0)} active jobs. Click to view job records.`}
              />
              <span className="mt-2 text-xs font-bold text-on-surface">
                {Number(completionHealth.jobsCompleted || 0)} / {Number(completionHealth.jobsTotal || 0)}
              </span>
              <span className="text-[10px] text-on-surface-variant group-hover:text-primary group-hover:underline">
                View Jobs &rarr;
              </span>
            </div>
          </div>
          <div className="mt-6 flex items-end justify-between border-t border-outline-variant/10 pt-4">
            <AppTooltip
              content={
                completionHasComparison
                  ? 'Compared with the immediately previous equal-length period.'
                  : 'No previous-period data found. Showing 0.0% delta.'
              }
            >
              <div className={`flex items-center gap-2 ${completionTrendTone}`}>
                <span className="material-symbols-outlined text-sm">{completionTrendIcon}</span>
                <span className="text-xs font-bold">
                  {completionGrowth >= 0 ? '+' : ''}
                  {formatPercent(completionGrowth)} vs Prev Period
                </span>
              </div>
            </AppTooltip>
            <button type="button" className="text-xs font-bold text-primary hover:underline" onClick={actions.openBreakdown}>
              Status Breakdown
            </button>
          </div>
        </div>

        {/* Cost vs. Billing */}
        {canViewFinancial ? <>
        <div className="rounded-lg border border-outline-variant/10 bg-surface-container-lowest p-6 shadow-sm md:col-span-8">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="mb-1 font-headline text-lg font-bold">Cost vs. Billing</h3>
              <p className="text-sm text-on-surface-variant">Projected margin analysis by period (click bar to drill down)</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 rounded-lg border border-outline-variant/20 bg-surface-container-low p-0.5">
                {[['monthly', 'Monthly'], ['weekly', 'Weekly'], ['project', 'Project-wise']].map(([value, label]) => (
                  <button key={value} type="button" onClick={() => actions.setCostBillingGroup(value)} className={`rounded px-2 py-1 text-xs font-semibold ${state.costBillingGroup === value ? 'bg-white text-primary shadow-sm' : 'text-on-surface-variant'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 rounded-lg border border-outline-variant/20 bg-surface-container-low p-0.5">
                <button
                  type="button"
                  onClick={() => actions.setCostBillingView('chart')}
                  className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold transition-all ${state.costBillingView === 'chart' ? 'bg-primary text-white shadow-sm' : 'text-on-surface-variant hover:text-primary'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">bar_chart</span>
                  Chart
                </button>
                <button
                  type="button"
                  onClick={() => actions.setCostBillingView('table')}
                  className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold transition-all ${state.costBillingView === 'table' ? 'bg-primary text-white shadow-sm' : 'text-on-surface-variant hover:text-primary'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">table_chart</span>
                  Table
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-full" style={{ background: BILLING_SERIES_COLOR }} />
                  <span className="text-xs font-medium">Billing</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-full" style={{ background: COST_SERIES_COLOR }} />
                  <span className="text-xs font-medium">Cost</span>
                </div>
              </div>
            </div>
          </div>

          {state.costBillingView === 'table' ? (
            <div className="overflow-x-auto max-h-52">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-outline-variant/20 bg-surface-container-low text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                    <th className="p-2.5">Period</th>
                    <th className="p-2.5 text-right">Billing</th>
                    <th className="p-2.5 text-right">Direct Cost</th>
                    <th className="p-2.5 text-right">Resource Cost</th>
                    <th className="p-2.5 text-right">Other Cost</th>
                    <th className="p-2.5 text-right">Margin</th>
                    <th className="p-2.5 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {costSeries.map((row) => (
                    <tr key={row.month} className="border-b border-outline-variant/10 hover:bg-surface-container-low/50">
                      <td className="p-2.5 font-bold text-on-surface">{row.month}</td>
                      <td className="p-2.5 text-right font-mono">{formatCurrency(row.billing)}</td>
                      <td className="p-2.5 text-right font-mono">{formatCurrency(row.directCost)}</td>
                      <td className="p-2.5 text-right font-mono">{formatCurrency(row.resourceCost ?? row.cost)}</td>
                      <td className="p-2.5 text-right font-mono">{formatCurrency(row.otherCost)}</td>
                      <td className={`p-2.5 text-right font-mono font-bold ${Number(row.margin || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {formatCurrency(row.margin)}
                      </td>
                      <td className="p-2.5 text-center">
                        <button
                          type="button"
                          className="text-xs font-bold text-primary hover:underline"
                          onClick={() => {
                            if (row.projectId) actions.setProjectId(String(row.projectId))
                            else actions.openFinancial(row.periodStart, row.periodEnd)
                          }}
                        >
                          Drill Down
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={costSeries}
                  barGap={4}
                  className="cursor-pointer"
                  onClick={(data) => {
                    if (data?.activePayload?.[0]?.payload) {
                      const p = data.activePayload[0].payload
                      if (p.projectId) actions.setProjectId(String(p.projectId))
                      else actions.openFinancial(p.periodStart, p.periodEnd)
                    }
                  }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(67, 70, 83, 0.18)" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tickMargin={8} />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatCompactCurrency}
                    width={65}
                    stroke="rgba(67, 70, 83, 0.6)"
                    fontSize={11}
                  />
                  <RechartsTooltip cursor={{ fill: 'rgba(0,71,171,0.05)' }} content={<CostVsBillingTooltip />} />
                  <Bar dataKey="cost" fill={COST_SERIES_COLOR} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="billing" fill={BILLING_SERIES_COLOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="mt-3 text-[11px] text-on-surface-variant">Billing uses project budget due in the selected period. Resource cost uses approved timesheet hours Ã— hourly CTC; no separate direct/other cost source is currently recorded, so those columns remain zero.</p>
        </div>

        {/* Surplus Trend */}
        <div className="relative overflow-hidden rounded-lg border border-outline-variant/10 bg-surface-container-lowest p-6 shadow-sm md:col-span-7">
          <div className="relative z-10">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-headline text-lg font-bold">Surplus Trend</h3>
              <div className="inline-flex items-center gap-1.5 rounded-md bg-surface-container-low px-2.5 py-1 text-[11px] text-on-surface-variant">
                <span className="font-bold text-primary">Formula:</span>
                <span>Net Surplus = Inflow / Receivable – Approved Project Cost</span>
              </div>
            </div>
            <p className="mb-4 text-sm text-on-surface-variant">Net cash by period bucket (click point to drill down to financials)</p>
            <div className="mb-4 flex flex-wrap items-baseline gap-4">
              <span className="font-headline text-4xl font-extrabold text-primary">{formatCompactCurrency(surplusTrend.headlineSurplus)}</span>
              {bestBucket ? <div className="rounded bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">Best {bestBucket.label}: {formatCompactCurrency(bestBucket.net)}</div> : null}
              {worstBucket ? <div className="rounded bg-rose-50 px-2 py-1 text-xs font-bold text-rose-700">Worst {worstBucket.label}: {formatCompactCurrency(worstBucket.net)}</div> : null}
              <div className="rounded bg-primary/5 px-2 py-1 text-xs font-bold text-primary">Previous: {formatCompactCurrency(surplusTrend.previousPeriodSurplus)} Â· Variance: {formatCompactCurrency(surplusTrend.varianceVsPrevious)}</div>
            </div>
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={surplusSeries}
                  margin={{
                    left: 12,
                    right: 12,
                    top: 6,
                    bottom: 6,
                  }}
                  className="cursor-pointer"
                  onClick={(data) => {
                    if (data?.activePayload?.[0]?.payload) {
                      const p = data.activePayload[0].payload
                      actions.openDetails(`Surplus Details - ${p.month}`, 'financial', p.periodStart, p.periodEnd)
                    }
                  }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(67, 70, 83, 0.16)" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tickMargin={8} minTickGap={18} interval="preserveStartEnd" />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatCompactCurrency}
                    width={65}
                    stroke="rgba(67, 70, 83, 0.6)"
                    fontSize={11}
                  />
                  <RechartsTooltip cursor={false} content={<SurplusTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="net"
                    stroke="var(--primary-container)"
                    strokeWidth={3}
                    dot={{ r: 3, cursor: 'pointer', fill: 'var(--primary-container)' }}
                    activeDot={{ r: 6, cursor: 'pointer', fill: 'var(--primary)' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        </> : null}

        {/* Resource Load */}
        <div className="rounded-lg border border-outline-variant/10 bg-surface-container-lowest p-6 shadow-sm md:col-span-5">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h3 className="mb-1 font-headline text-lg font-bold">Resource Load</h3>
              <p className="text-sm text-on-surface-variant">Deployment intensity heatmap (click cell to view timesheets)</p>
            </div>
            <div className="flex items-center gap-1 rounded-lg border border-outline-variant/20 bg-surface-container-low p-0.5">
              <button
                type="button"
                onClick={() => actions.setResourceLoadView('chart')}
                className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold transition-all ${state.resourceLoadView === 'chart' ? 'bg-primary text-white shadow-sm' : 'text-on-surface-variant hover:text-primary'}`}
              >
                <span className="material-symbols-outlined text-[14px]">grid_view</span>
                Heatmap
              </button>
              <button
                type="button"
                onClick={() => actions.setResourceLoadView('table')}
                className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold transition-all ${state.resourceLoadView === 'table' ? 'bg-primary text-white shadow-sm' : 'text-on-surface-variant hover:text-primary'}`}
              >
                <span className="material-symbols-outlined text-[14px]">table_chart</span>
                Table
              </button>
            </div>
          </div>

          {state.resourceLoadView === 'table' ? (
            <div className="max-h-52 overflow-y-auto">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-outline-variant/20 bg-surface-container-low text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                    <th className="p-2">Employee / Team</th>
                    <th className="p-2 text-right">Allocated</th>
                    <th className="p-2 text-right">Used</th>
                    <th className="p-2 text-right">Capacity</th>
                    <th className="p-2 text-right">Util %</th>
                    <th className="p-2 text-center">Band</th>
                    <th className="p-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {resourceUsers.map((row, idx) => (
                    <tr key={`${row.userId}-${idx}`} className="border-b border-outline-variant/10 hover:bg-surface-container-low/50">
                      <td className="p-2 font-medium text-on-surface">{row.userName}<span className="block text-[10px] text-on-surface-variant">{row.departmentName}</span></td>
                      <td className="p-2 text-right font-mono">{Number(row.allocatedHours || 0).toFixed(1)}h</td>
                      <td className="p-2 text-right font-mono">{Number(row.usedHours || 0).toFixed(1)}h</td>
                      <td className="p-2 text-right font-mono">{Number(row.capacityHours || 0).toFixed(1)}h</td>
                      <td className="p-2 text-right font-mono font-bold">{Math.round(row.utilizationPercent || 0)}%</td>
                      <td className="p-2 text-center">
                        <span className="rounded-full bg-surface-container-low px-2 py-0.5 text-[10px] font-bold capitalize text-on-surface-variant">
                          {row.level}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <button
                          type="button"
                          className="text-xs font-bold text-primary hover:underline"
                          onClick={() => actions.openTimesheets()}
                        >
                          Timesheets
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-2">
                {heatmapRows.map((item, index) => {
                  const level = String(item.level || 'low')
                  const toneClass =
                    level === 'overallocated'
                      ? 'bg-rose-600'
                      : level === 'full'
                        ? 'bg-primary'
                        : level === 'high'
                          ? 'bg-primary/75'
                          : level === 'normal' || level === 'moderate'
                            ? 'bg-primary/45'
                            : 'bg-primary/15'
                  return (
                    <AppTooltip
                      key={`${item.date || 'day'}-${index}`}
                      content={`${item.date}: ${Number(item.loggedHours || 0).toFixed(2)}h logged / ${Number(item.dailyCapacity || 0).toFixed(2)}h capacity (${Math.round(item.utilization || 0)}% - ${level}). Click to drill down into timesheets.`}
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => actions.openTimesheets(item.periodStart || item.date, item.periodEnd || item.date)}
                        onKeyDown={(e) => e.key === 'Enter' && actions.openTimesheets(item.periodStart || item.date, item.periodEnd || item.date)}
                        className={`h-8 rounded-sm ${toneClass} cursor-pointer transition-all hover:scale-110 hover:ring-2 hover:ring-primary`}
                      />
                    </AppTooltip>
                  )
                })}
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                <span>Low (&lt;40%)</span>
                <div className="flex items-center gap-1.5">
                  <div className="h-3 w-3 rounded-sm bg-primary/15" title="Low (<40%)" />
                  <div className="h-3 w-3 rounded-sm bg-primary/45" title="Normal (40-70%)" />
                  <div className="h-3 w-3 rounded-sm bg-primary/75" title="High (70-90%)" />
                  <div className="h-3 w-3 rounded-sm bg-primary" title="Full Capacity (90-100%)" />
                  <div className="h-3 w-3 rounded-sm bg-rose-600" title="Overallocated (>100%)" />
                </div>
                <span>Overallocated (&gt;100%)</span>
              </div>
            </>
          )}
        </div>

        {canViewFinancial ? (
          <div className="rounded-lg border border-outline-variant/10 bg-surface-container-lowest p-6 shadow-sm md:col-span-12">
            <div className="mb-4 flex items-center justify-between">
              <div><h3 className="font-headline text-lg font-bold">Payment Forecast</h3><p className="text-sm text-on-surface-variant">Expected receipts, overdue receivables, approved labor cost and projected surplus.</p></div>
              <button type="button" className="text-xs font-bold text-primary" onClick={() => actions.openFinancial()}>View invoices & receivables</button>
            </div>
            <div className="grid gap-3 md:grid-cols-4">
              {[['Expected Inflow', paymentForecast.expectedInflow], ['Overdue Receivables', paymentForecast.overdueReceivables], ['Projected Cost', paymentForecast.projectedCost], ['Projected Surplus', paymentForecast.projectedSurplus]].map(([label, value]) => <button key={label} type="button" onClick={() => actions.openFinancial()} className="rounded-lg bg-surface-container-low p-4 text-left"><span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">{label}</span><div className="mt-1 text-xl font-extrabold text-primary">{formatCurrency(value)}</div></button>)}
            </div>
            {Array.isArray(paymentForecast.rows) && paymentForecast.rows.length ? <div className="mt-4 overflow-x-auto"><table className="w-full text-left text-xs"><thead><tr><th className="p-2">Customer / Project</th><th className="p-2">Due Date</th><th className="p-2 text-right">Expected</th><th className="p-2 text-right">Overdue</th><th className="p-2 text-right">Aging Days</th><th className="p-2">Status</th></tr></thead><tbody>{paymentForecast.rows.slice(0, 5).map((row) => <tr key={row.projectId} className="border-t border-outline-variant/10"><td className="p-2 font-bold">{row.customer} · {row.projectName}</td><td className="p-2">{row.dueDate || '-'}</td><td className="p-2 text-right">{formatCurrency(row.expectedInflow)}</td><td className="p-2 text-right">{formatCurrency(row.overdueAmount)}</td><td className="p-2 text-right">{row.agingDays}</td><td className="p-2">{row.paymentStatus}</td></tr>)}</tbody></table></div> : null}
          </div>
        ) : null}

      </div>

      {/* Completion Breakdown Modal */}
      {state.isBreakdownOpen ? (
        <div className="analytics-project-drawer-overlay" onClick={actions.closeBreakdown} role="presentation">
          <aside className="analytics-project-drawer-panel" onClick={(event) => event.stopPropagation()}>
            <div className="analytics-project-drawer-header">
              <div>
                <h3 className="font-headline text-3xl font-bold text-primary">Completion Breakdown</h3>
                <p className="text-sm text-on-surface-variant">Task/job split and lagging work items.</p>
              </div>
              <button type="button" className="material-symbols-outlined text-on-surface-variant" onClick={actions.closeBreakdown}>
                close
              </button>
            </div>
            {state.isLoadingBreakdown ? <p className="analytics-project-status">Loading breakdown...</p> : null}
            {state.breakdownError ? <p className="analytics-project-status analytics-project-status-error">{state.breakdownError}</p> : null}
            {!state.isLoadingBreakdown && !state.breakdownError ? (
              <div className="analytics-project-drawer-content">
                <section>
                  <h4 className="analytics-project-drawer-subtitle">Task Status</h4>
                  <div className="analytics-project-chip-grid">
                    {(state.breakdown?.taskStatusSplit || []).map((item) => (
                      <span key={`task-${item.status}`} className="analytics-project-chip">
                        {item.status}: {item.count}
                      </span>
                    ))}
                  </div>
                </section>
                <section>
                  <h4 className="analytics-project-drawer-subtitle">Job Status</h4>
                  <div className="analytics-project-chip-grid">
                    {(state.breakdown?.jobStatusSplit || []).map((item) => (
                      <span key={`job-${item.status}`} className="analytics-project-chip">
                        {item.status}: {item.count}
                      </span>
                    ))}
                  </div>
                </section>
                <section>
                  <h4 className="analytics-project-drawer-subtitle">Top Lagging Items</h4>
                  <div className="analytics-project-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Identifier</th>
                          <th>Project</th>
                          <th>Overdue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(state.breakdown?.topLaggingItems || []).length ? (
                          state.breakdown.topLaggingItems.map((item, index) => (
                            <tr key={`${item.itemType || 'ITEM'}-${item.itemCode || index}-${index}`}>
                              <td>{item.itemType}</td>
                              <td>{item.itemCode}</td>
                              <td>{item.projectCode}</td>
                              <td>{item.daysOverdue}d</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4} className="analytics-project-empty-cell">
                              No lagging items for selected filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            ) : null}
          </aside>
        </div>
      ) : null}

      {/* Interactive Multi-Level Drilldown Drawer */}
      {state.isDetailsOpen ? (
        <div className="analytics-project-drawer-overlay" onClick={actions.closeDetails} role="presentation">
          <aside className="analytics-project-drawer-panel" onClick={(event) => event.stopPropagation()}>
            <div className="analytics-project-drawer-header">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="font-headline text-2xl font-bold text-primary">{state.drillTitle}</h3>
                  {state.drillPeriodFrom && state.drillPeriodTo ? (
                    <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
                      Period: {state.drillPeriodFrom} → {state.drillPeriodTo}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-sm text-on-surface-variant">
                  Tracing management records across projects, jobs, tasks, timesheets, and invoices.
                </p>
              </div>
              <button
                type="button"
                className="material-symbols-outlined text-on-surface-variant hover:text-on-surface"
                onClick={actions.closeDetails}
              >
                close
              </button>
            </div>
            <div className="analytics-project-drawer-content">
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-semibold text-on-surface-variant">
                <span>Project Analytics</span><span>/</span><span className="text-primary">{state.drillTitle}</span>
                <button type="button" className="ml-auto rounded border border-outline-variant/30 px-2.5 py-1.5 text-primary" onClick={actions.closeDetails}>Back</button>
                <button type="button" className="rounded border border-outline-variant/30 px-2.5 py-1.5 text-primary" onClick={() => {
                  if (state.drillLevel === 'project') actions.closeDetails()
                  else if (state.drillLevel === 'job') actions.openDetails('Project Overview', 'project', state.drillPeriodFrom, state.drillPeriodTo)
                  else actions.openJobs(state.drillPeriodFrom, state.drillPeriodTo)
                }}>Up One Level</button>
              </div>
              {/* Navigation Tabs with Active Underline and Right-Aligned Solid Export CSV */}
              <div className="analytics-project-drawer-nav">
                <button
                  type="button"
                  className={`analytics-project-drawer-tab ${state.drillLevel === 'project' ? 'active' : ''}`}
                  onClick={() => actions.openDetails('Project Overview', 'project', state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  Project Overview
                </button>
                <button
                  type="button"
                  className={`analytics-project-drawer-tab ${state.drillLevel === 'job' ? 'active' : ''}`}
                  onClick={() => actions.openJobs(state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  Jobs
                </button>
                <button
                  type="button"
                  className={`analytics-project-drawer-tab ${state.drillLevel === 'task' ? 'active' : ''}`}
                  onClick={() => actions.openTasks(state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  Tasks
                </button>
                <button
                  type="button"
                  className={`analytics-project-drawer-tab ${state.drillLevel === 'timesheet' ? 'active' : ''}`}
                  onClick={() => actions.openTimesheets(state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  Timesheet Activity
                </button>
                {canViewFinancial ? <button
                  type="button"
                  className={`analytics-project-drawer-tab ${state.drillLevel === 'financial' ? 'active' : ''}`}
                  onClick={() => actions.openFinancial(state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  Financial
                </button> : null}
                <button
                  type="button"
                  className="analytics-project-drawer-tab"
                  onClick={actions.resetFilters}
                >
                  Reset Drilldown
                </button>
                <button
                  type="button"
                  className="ml-auto rounded bg-primary px-3 py-2 text-xs font-bold text-white transition-opacity hover:opacity-95 disabled:opacity-60"
                  onClick={() => actions.exportCurrentDrilldown('csv')}
                  disabled={state.isExporting}
                >
                  {state.isExporting ? 'Exporting...' : 'Export CSV'}
                </button>
                <button type="button" className="rounded border border-primary px-3 py-2 text-xs font-bold text-primary disabled:opacity-60" onClick={() => actions.exportCurrentDrilldown('pdf')} disabled={state.isExporting}>Export PDF</button>
              </div>

              {state.drilldownError ? <p className="mb-4 text-sm text-error">{state.drilldownError}</p> : null}
              {state.isLoadingDrilldown ? <p className="mb-4 text-sm text-on-surface-variant">Loading drill-down data...</p> : null}

              <AnalyticsProjectTable
                className="analytics-project-list-card--drawer"
                columns={[
                  ...drillColumns.map(titleCase),
                  ...(['project', 'job', 'task', 'timesheet', 'financial'].includes(state.drillLevel) ? ['Action'] : [])
                ]}
                rows={drillRows}
                rowKey={(row, idx) => `${state.drillLevel}-${row.id || row.projectCode || row.jobCode || row.taskCode || idx}`}
                emptyMessage={state.isLoadingDrilldown ? 'Loading details...' : 'No drill-down records match the current filters.'}
                renderRow={(row) => (
                  <>
                    {drillColumns.map((key) => {
                      const value = row[key]
                      if (key === 'status' || key === 'priority' || key === 'approvalStatus' || key === 'paymentStatus') {
                        return (
                          <td key={key}>
                            <span className={`analytics-project-list-badge ${key === 'paymentStatus' && value === 'Overdue' ? 'analytics-project-list-count--alert' : ''}`}>
                              {value || '-'}
                            </span>
                          </td>
                        )
                      }
                      const isCurrency = ['budget', 'received', 'laborCost', 'outstanding', 'netMargin'].includes(key)
                      const isHours = ['estimatedHours', 'loggedHours', 'hours'].includes(key)
                      const isPercent = key === 'completion'
                      let formatted = value ?? '-'
                      if (typeof value === 'number') {
                        if (isCurrency) formatted = formatCurrency(value)
                        else if (isHours) formatted = `${value.toFixed(1)}h`
                        else if (isPercent) formatted = `${Math.round(value)}%`
                        else formatted = value.toLocaleString('en-IN')
                      }
                      const isPrimary = ['projectCode', 'projectName', 'jobCode', 'taskCode'].includes(key)
                      const isNumeric = typeof value === 'number' || isCurrency || isHours
                      const isMargin = key === 'netMargin'
                      const toneClass = isMargin ? (Number(value || 0) >= 0 ? 'analytics-project-list-positive' : 'analytics-project-list-negative') : ''
                      return (
                        <td
                          key={key}
                          className={`${isPrimary ? 'analytics-project-list-primary' : ''} ${isNumeric ? 'analytics-project-list-number' : ''} ${toneClass}`}
                        >
                          {formatted}
                        </td>
                      )
                    })}
                    {state.drillLevel === 'job' && row.id ? (
                      <td>
                        <button
                          type="button"
                          className="analytics-project-list-action"
                          onClick={() => navigate(`/nerve/job/entry?id=${row.id}`)}
                        >
                          View Job <span className="material-symbols-outlined">arrow_forward</span>
                        </button>
                      </td>
                    ) : null}
                    {state.drillLevel === 'project' && row.projectCode ? (
                      <td>
                        <button
                          type="button"
                          className="analytics-project-list-action"
                          onClick={() => actions.openJobs(state.drillPeriodFrom, state.drillPeriodTo)}
                        >
                          View Jobs <span className="material-symbols-outlined">arrow_forward</span>
                        </button>
                      </td>
                    ) : null}
                    {state.drillLevel === 'task' && row.id ? <td><button type="button" className="analytics-project-list-action" onClick={() => navigate(`/nerve/task/entry?id=${row.id}`)}>View Task <span className="material-symbols-outlined">arrow_forward</span></button></td> : null}
                    {state.drillLevel === 'timesheet' && row.id ? <td><button type="button" className="analytics-project-list-action" onClick={() => navigate(`/nerve/timesheet/entry?id=${row.id}&mode=view`)}>View Timesheet <span className="material-symbols-outlined">arrow_forward</span></button></td> : null}
                    {state.drillLevel === 'financial' && row.id ? <td><button type="button" className="analytics-project-list-action" onClick={() => navigate(`/nerve/project/detail?id=${row.id}`)}>View Project <span className="material-symbols-outlined">arrow_forward</span></button></td> : null}
                  </>
                )}
              />
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  )
}
